import {
  ContentfulPlan,
  getPlanBySlug,
  getPlansSlugs,
  planCollectionNames,
} from '@smarty-nx/contentful'
import { Plan } from '@smarty-nx/data'
import { logErrorAndNotify } from '@smarty-nx/debug-utils'
import { colors } from '@smarty-nx/ds'
import _ from 'lodash'

import { PlanDataAndMeta, PlanMeta } from './plans.types'

export const checkDataAmountIsDiff = (
  live: Plan,
  local: PlanMeta
): [boolean, number] => {
  if (local.data_number === Infinity) {
    return [false, local.data_number]
  }

  if (!live.voucher) {
    return [false, local.data_number]
  }

  if (typeof live.voucher.data_amount !== 'number') {
    return [false, local.data_number]
  }

  if (local.data_number !== live.voucher.data_amount) {
    return [true, live.voucher.data_amount]
  }

  return [false, live.voucher.data_amount]
}

export const getGroupPlanBySlug = (
  plans: Plan[],
  planSlug: string
): Plan | null => {
  const match = plans && plans.find((p) => p.slug === planSlug)

  return match || null
}

const defaultFn = (value: string): string => value

const fnForColour = (value: string): string => {
  if (colors?.[value]) {
    return colors[value]
  }
  throw new Error(`Missing color '${value}' in theme.json colours!`)
}

interface CheckFieldParams {
  data: object & { slug?: string }
  key: string
  defaultValue: string | number | boolean | null | undefined
  cb?: (value: string) => string
}

export const checkField = ({
  data,
  key,
  defaultValue,
  cb = defaultFn,
}: CheckFieldParams): string | number | boolean | null | undefined => {
  try {
    const value = _.get(data, key)
    if (value) {
      return cb(value as string)
    }
    const message = `Missing key '${key}' for ${data.slug} in Contentful CMS!`
    logErrorAndNotify(message)
    return defaultValue
  } catch (error: unknown) {
    const err = error as Error
    logErrorAndNotify(err.message, error)
    return defaultValue
  }
}

export const buildPlanMetaObject = (slug: string): PlanMeta | null => {
  const planData: ContentfulPlan | undefined = getPlanBySlug(slug)

  if (!planData) {
    return null
  }

  return {
    id: planData?.id,
    name: planData?.name,
    slug: planData?.slug,
    title: planData.title || '',
    description: planData?.description,
    color: planData?.planColor?.colourCode,
    colorLight: planData?.planColorPale?.colourCode,
    data: planData.data || '',
    data_discount: planData.dataDiscount || null,
    data_number: 0,
    plan_is_data_only: false,
    usesYellowAssets: planData.usesYellowAssets,
  }
}

export const matchPlanMeta = (
  plans: Plan[],
  slug: string
): PlanDataAndMeta | null => {
  const groupPlan = getGroupPlanBySlug(plans, slug)

  if (!groupPlan) return null

  let planMeta: PlanMeta | null = buildPlanMetaObject(slug)

  planMeta = {
    ...planMeta,
    name: checkField({
      data: groupPlan,
      key: 'name',
      defaultValue: planMeta?.name,
    }),
    data_number: groupPlan.dataAllowanceGB,
    description: checkField({
      data: groupPlan,
      key: 'description',
      defaultValue: planMeta?.description,
    }),
    color: checkField({
      data: groupPlan,
      key: 'style.planColor',
      defaultValue: planMeta?.color,
      cb: fnForColour,
    }),
    colorLight: checkField({
      data: groupPlan,
      key: 'style.planColorPale',
      defaultValue: planMeta?.colorLight,
      cb: fnForColour,
    }),
    plan_is_data_only: groupPlan.planIsDataOnly,
  } as PlanMeta

  return { priceData: groupPlan, planMeta }
}

export const returnDataWithUnit = (dataValue: number): string =>
  dataValue < 1 ? `${dataValue * 1000}MB` : `${dataValue}GB`

export const getFamilyPlansSlugs = (): string[] =>
  getPlansSlugs(planCollectionNames.FAMILY_PLANS)
